import { useGlobalSearchParams, usePathname, useSegments } from "expo-router";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { isTest } from "@/config/env";
import { useCurrentConversationId } from "@/hooks/useCurrentConversationId";
import initOurs from "@/ours";
import type { OursClient } from "@/ours.types";
import initSegment, { Segment } from "@/segment";
import { captureException } from "@/sentry";

const warnUninitializedSegment = () => {
  if (isTest) return;
  console.warn("Segment not initialized");
};
const UninitializedSegment = {
  track: warnUninitializedSegment,
  screen: warnUninitializedSegment,
  identify: warnUninitializedSegment,
  alias: warnUninitializedSegment,
  reset: warnUninitializedSegment,
};

// Isolate Ours calls so a failure there can never break Segment or a call site.
const safeOurs = (fn: () => void) => {
  try {
    fn();
  } catch (error) {
    captureException(error);
  }
};

// Wraps the Segment client so track/screen/identify/reset also fan out to Ours
// Privacy. Segment runs first and its behavior (e.g. web Bing UET interception)
// is untouched; each method keeps the Segment client's exact signature, casting
// only at the Ours boundary. Every call site keeps the same method surface.
const makeFacade = (segment: Segment, ours: OursClient | null): Segment => {
  const track = (
    ...args: Parameters<Segment["track"]>
  ): ReturnType<Segment["track"]> => {
    const result = segment.track(...args);
    safeOurs(() =>
      ours?.track(
        args[0] as string,
        args[1] as Record<string, unknown> | undefined,
      ),
    );
    return result;
  };
  const screen = (
    ...args: Parameters<Segment["screen"]>
  ): ReturnType<Segment["screen"]> => {
    const result = segment.screen(...args);
    safeOurs(() =>
      ours?.screen(
        args[0] as string,
        args[1] as Record<string, unknown> | undefined,
      ),
    );
    return result;
  };
  const identify = (
    ...args: Parameters<Segment["identify"]>
  ): ReturnType<Segment["identify"]> => {
    const result = segment.identify(...args);
    safeOurs(() => {
      const idOrObj = args[0];
      if (typeof idOrObj === "string") {
        ours?.identify(idOrObj, args[1] as Record<string, unknown> | undefined);
      } else if (idOrObj && typeof idOrObj === "object") {
        const { anonymousId, ...rest } = idOrObj as {
          anonymousId?: string;
        } & Record<string, unknown>;
        ours?.identify(anonymousId, {
          ...rest,
          ...((args[1] as Record<string, unknown>) ?? {}),
        });
      }
    });
    return result;
  };
  // Ours has no alias; identity resolves via externalId. Segment-only.
  const alias = (
    ...args: Parameters<Segment["alias"]>
  ): ReturnType<Segment["alias"]> => segment.alias(...args);
  const reset = (
    ...args: Parameters<Segment["reset"]>
  ): ReturnType<Segment["reset"]> => {
    const result = segment.reset(...args);
    safeOurs(() => ours?.reset());
    return result;
  };
  return { ...segment, track, screen, identify, alias, reset } as Segment;
};

const AnalyticsContext = createContext<Segment>(UninitializedSegment as any);
const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const [segment, setSegment] = useState<Segment>(UninitializedSegment as any);
  const [ours, setOurs] = useState<OursClient | null>(null);
  useEffect(() => {
    const s = initSegment();
    if (s) setSegment(s);
    // Ours init is async; the facade no-ops Ours until it resolves.
    initOurs().then((o) => {
      if (o) setOurs(o);
    });
  }, []);
  const value = useMemo(() => makeFacade(segment, ours), [segment, ours]);
  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

const useTrackPageLoad = () => {
  const { screen } = useAnalytics();
  const segments = useSegments();
  const pathname = usePathname();
  const params = useGlobalSearchParams();
  useEffect(() => {
    screen(segments.join("/") || "/", { pathname, params });
  }, [segments, screen, pathname, params]);
};

const useEventContext = () => {
  const pathnameWithoutParams = usePathname();
  const params = useGlobalSearchParams();
  let segments = useSegments();

  // Remove conversation/[id], if present, from canonical location for compatibility with events prior to changing from url param
  const conversationSegmentIdx = segments.findIndex(
    (s) => s === "conversation",
  );
  if (conversationSegmentIdx !== -1) {
    segments = segments
      .slice(0, conversationSegmentIdx)
      .concat(segments.slice(conversationSegmentIdx + 2)) as any;
  }

  const conversationId = useCurrentConversationId();

  const canonicalLocation = segments.join("/"); // This "anonymizes" url parameters. e.g., all events on any conversation page are logged as "/[conversationId]"
  const serializedParams = new URLSearchParams(
    params as Record<string, string> | URLSearchParams,
  ).toString();
  const pathname = `${pathnameWithoutParams}${serializedParams ? `?${serializedParams}` : ""}`;

  return useMemo(
    () => ({
      conversationId,
      location: canonicalLocation,
      pathname,
    }),
    [conversationId, canonicalLocation, pathname],
  );
};

const useAnalytics = () => useContext(AnalyticsContext);

export {
  AnalyticsProvider,
  makeFacade,
  useAnalytics,
  useEventContext,
  useTrackPageLoad,
};
